/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable unused-imports/no-unused-vars */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Skeleton, IconButton, Tooltip, Divider, Fade } from '@mui/material';
import { ZoomIn, ZoomOut, Refresh } from '@mui/icons-material';
import { Trash } from '@/components/icons';
import Typography from '@/components/typography/typography';

const HtmlThumbnailPreview = ({ 
  template, 
  width = 300, 
  height = 200, 
  scale = 0.25,
  showControls = false,
  onClick = null,
  selectedTemplateId = '',
  isFetching = false,
  onDelete,
  minScale = 0.1,
  maxScale = 1,
  scaleStep = 1.2,
  loadingDelay = 500,
  refreshDelay = 300,
  normalView = false,
}) => {
  const [isLoading, setIsLoading] = useState(isFetching || true);
  const [currentScale, setCurrentScale] = useState(scale);
  const [error, setError] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const loadingTimeoutRef = useRef(null);
  const refreshTimeoutRef = useRef(null);

  // Calculate dimensions based on scale
  const scaledWidth = width / currentScale;
  const scaledHeight = height / currentScale;

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  // Handle initial loading
  useEffect(() => {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }
    
    loadingTimeoutRef.current = setTimeout(() => {
      setIsLoading(false);
    }, loadingDelay);

    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, [template.contents, loadingDelay]);

  // Handle external loading state
  useEffect(() => {
    setIsLoading(isFetching);
  }, [isFetching]);

  // Memoized event handlers
  const handleZoomIn = useCallback(() => {
    setCurrentScale(prev => Math.min(prev * scaleStep, maxScale));
  }, [scaleStep, maxScale]);

  const handleZoomOut = useCallback(() => {
    setCurrentScale(prev => Math.max(prev / scaleStep, minScale));
  }, [scaleStep, minScale]);

  const handleRefresh = useCallback(() => {
    setIsLoading(true);
    setError(null);
    
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    
    refreshTimeoutRef.current = setTimeout(() => {
      setIsLoading(false);
    }, refreshDelay);
  }, [refreshDelay]);

  const handleClick = useCallback(() => {
    if (onClick && !isLoading) {
      onClick(template);
    }
  }, [onClick, template, isLoading]);

  const handleDelete = useCallback((e) => {
    e.stopPropagation(); // Prevent triggering onClick
    if (onDelete && !template?.is_default) {
      onDelete(template);
    }
  }, [onDelete, template]);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <Box 
        sx={{ 
          width, 
          height,
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Skeleton 
          variant="rectangular" 
          width="100%" 
          height="100%" 
          animation="wave"
        />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box 
        sx={{ 
          width, 
          height,
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'error.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'error.light',
          color: 'error.contrastText'
        }}
      >
        Failed to load template
      </Box>
    );
  }

  const isSelected = selectedTemplateId === template._id;
  const canZoomIn = currentScale < maxScale;
  const canZoomOut = currentScale > minScale;
  const canDelete = !template?.is_default;

  return (
    <Box 
      sx={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Main Container */}
      <Box
        ref={containerRef}
        onClick={handleClick}
        sx={(theme) => ({
          width,
          height,
          border: '1px solid',
          borderRadius: 1,
          overflow: 'hidden',
          position: 'relative',
          cursor: onClick ? 'pointer' : 'default',
          backgroundColor: '#fff',
          // boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': onClick ? {
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            transform: 'translateY(-1px)',
          } : {},
          borderColor: normalView && isSelected ? theme.palette.theme.primary[800] : 'grey.300',
          borderWidth: isSelected ? 2 : 1,
        })}
      >
        {/* Scaled Content Wrapper */}
        <Box
          sx={{
            width: scaledWidth,
            height: scaledHeight,
            transform: `scale(${currentScale})`,
            transformOrigin: 'top left',
            position: 'absolute',
            top: 0,
            left: 0,
            overflow: 'hidden',
          }}
        >
          {/* HTML Content */}
          <Box
            ref={contentRef}
            sx={{
              width: '100%',
              height: '100%',
              userSelect: 'none',
              pointerEvents: 'none',
              pt: 2,
              '& *': {
                maxWidth: '100% !important',
                boxSizing: 'border-box !important',
              },
              // Reset common problematic styles
              '& body': {
                margin: '0 !important',
                padding: '0 !important',
                overflow: 'hidden !important',
              },
              // Handle images
              '& img': {
                maxWidth: '100% !important',
                height: 'auto !important',
              },
              // Handle tables
              '& table': {
                width: '100% !important',
                tableLayout: 'fixed !important',
              },
              // Prevent absolute positioning issues
              '& *[style*="position: absolute"]': {
                position: 'relative !important',
              },
              '& .email-container': {
                width: '85% !important',
              }
            }}
            dangerouslySetInnerHTML={{ __html: template.contents }}
          />
        </Box>

        {/* Loading Overlay */}
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255,255,255,0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
            }}
          >
            <Skeleton variant="rectangular" width="90%" height="90%" />
          </Box>
        )}

        {/* Selection Indicator */}
        {template?.is_default && (
          <Box
            sx={(theme) => ({
              position: 'absolute',
              top: 8,
              left: 8,
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: theme.palette.theme?.primary[800],
              zIndex: 3,
            })}
          />
        )}
      </Box>
      {template?.name && <Typography type='s3' weight='medium' color='theme.secondary.1000' align='center' sx={{width, mt:0.5}}>{template?.name}</Typography>}

      {/* Hover Controls */}
      {showControls && (
        <Fade in={isHovered} timeout={200}>
          <Box
            sx={{
              position: 'absolute',
              top: -8,
              right: -8,
              display: 'flex',
              gap: 0.5,
              zIndex: 4,
              backgroundColor: 'rgba(255,255,255,0.95)',
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 2,
              padding: 0.5,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              backdropFilter: 'blur(4px)',
            }}
          >
            <Tooltip title={`Zoom In (${Math.round(currentScale * 100)}%)`} arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={handleZoomIn}
                  disabled={!canZoomIn}
                  sx={{
                    backgroundColor: 'transparent',
                    '&:hover': { 
                      backgroundColor: 'primary.light',
                      color: 'primary.contrastText' 
                    },
                    '&:disabled': {
                      opacity: 0.5
                    }
                  }}
                >
                  <ZoomIn fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Divider orientation="vertical" variant="middle" flexItem />

            <Tooltip title={`Zoom Out (${Math.round(currentScale * 100)}%)`} arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={handleZoomOut}
                  disabled={!canZoomOut}
                  sx={{
                    backgroundColor: 'transparent',
                    '&:hover': { 
                      backgroundColor: 'primary.light',
                      color: 'primary.contrastText' 
                    },
                    '&:disabled': {
                      opacity: 0.5
                    }
                  }}
                >
                  <ZoomOut fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Divider orientation="vertical" variant="middle" flexItem />

            <Tooltip title="Refresh Preview" arrow>
              <IconButton
                size="small"
                onClick={handleRefresh}
                sx={{
                  backgroundColor: 'transparent',
                  '&:hover': { 
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText' 
                  },
                }}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>

            {onDelete && (
              <>
                <Divider orientation="vertical" variant="middle" flexItem />
                <Tooltip 
                  title={canDelete ? "Delete Template" : "Cannot delete default template"} 
                  arrow
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleDelete}
                      disabled={!canDelete}
                      sx={{
                        backgroundColor: 'transparent',
                        '&:hover': !canDelete ? {} : { 
                          backgroundColor: 'error.light',
                          color: 'error.contrastText' 
                        },
                        '&:disabled': {
                          opacity: 0.5
                        }
                      }}
                    >
                      <Trash fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export { HtmlThumbnailPreview };
export default HtmlThumbnailPreview;